import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { EmailService } from '../../../core/services/email.service';
import { TranslationService } from '../../../core/services/translation.service';
import { LegalModalComponent } from '../legal-modal/legal-modal.component';

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, RouterModule, LegalModalComponent],
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
})
export class FooterComponent implements OnInit, OnDestroy {
  currentYear = new Date().getFullYear();
  infoEmail: string;
  officeEmail: string;
  private languageSubscription: Subscription | null = null;
  
  // Modal state
  showPrivacyModal = false;
  showTermsModal = false;
  showCookieModal = false;
  
  // Legal content - in a real app, this would come from a backend API
  privacyContent = `
    <h3>Privacy Policy</h3>
    <p>Last updated: ${new Date().toLocaleDateString()}</p>
    <p>MZM Homes ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how your personal information is collected, used, and disclosed by MZM Homes.</p>
    
    <h4>Information We Collect</h4>
    <p>We may collect personal information that you provide to us, such as your name, email address, phone number, and any other information you choose to provide when you:</p>
    <ul>
      <li>Fill out a form on our website</li>
      <li>Contact us via email or phone</li>
      <li>Subscribe to our newsletter</li>
      <li>Request information about our products and services</li>
    </ul>
    
    <h4>How We Use Your Information</h4>
    <p>We may use the information we collect for various purposes, including to:</p>
    <ul>
      <li>Provide, maintain, and improve our services</li>
      <li>Respond to your comments, questions, and requests</li>
      <li>Communicate with you about products, services, offers, and events</li>
      <li>Process and complete transactions</li>
      <li>Monitor and analyze trends, usage, and activities</li>
    </ul>
    
    <h4>Sharing of Information</h4>
    <p>We may share your personal information with:</p>
    <ul>
      <li>Service providers who perform services on our behalf</li>
      <li>Professional advisors, such as lawyers, auditors, and insurers</li>
      <li>Government authorities when required by law</li>
    </ul>
    
    <h4>Your Rights</h4>
    <p>Depending on your location, you may have certain rights regarding your personal information, such as the right to:</p>
    <ul>
      <li>Access the personal information we hold about you</li>
      <li>Correct inaccurate or incomplete information</li>
      <li>Request deletion of your personal information</li>
      <li>Restrict or object to our processing of your information</li>
      <li>Data portability</li>
    </ul>
    
    <h4>Contact Us</h4>
    <p>If you have any questions about this Privacy Policy, please contact us at:</p>
    <p>MZM Homes<br>
    Temes u. 18, 3528 Miskolc, HUNGARY<br>
    Email: info@mzmhomes.com</p>
  `;
  
  cookieContent = `
    <h3>Cookie Policy</h3>
    <p>Last updated: ${new Date().toLocaleDateString()}</p>
    
    <h4>What are Cookies</h4>
    <p>Cookies are small text files that most websites store on devices that users use to access the internet in order to identify individual devices that users have used to access. Their storage is under the complete control of the browser that the user has - the user can limit or disable the storage of cookies as desired. Cookies are not harmful and are always time-limited.</p>
    
    <h4>Why are cookies necessary?</h4>
    <p>They are essential for providing user-friendly web services. The interaction between the web user and the website is faster and easier with the help of cookies. With their help, the website remembers an individual's preferences and experiences, saving time and making browsing websites more efficient and friendly.</p>
    
    <h4>Some concrete examples of cookie usage:</h4>
    <ul>
      <li>To improve the user experience of the website, we adapt the display of content to visitors based on past visits</li>
      <li>Storing choices when creating a shortlist of devices and offers and comparing them</li>
      <li>On parts of websites where login is required, we keep you logged in</li>
      <li>For recognizing your device (computer, tablet, mobile phone), which allows adapting the display of content to your device</li>
      <li>For monitoring visits, which allows checking the effectiveness of content display and the relevance of advertisements and continuous improvement of websites</li>
      <li>They are essential for the operation of certain services (e.g., online banks, online stores, and other forms of e-commerce)</li>
    </ul>
    
    <h4>Disabling cookies</h4>
    <p>You decide whether to allow cookies to be stored on your device. Cookie settings can be controlled and changed in your web browser.</p>
    
    <p>For information on cookie settings, select the web browser you are using:</p>
    <ul>
      <li><a href="https://support.microsoft.com/en-us/help/17442/windows-internet-explorer-delete-manage-cookies" target="_blank">Internet Explorer</a></li>
      <li><a href="https://support.google.com/chrome/answer/95647?hl=en" target="_blank">Chrome</a></li>
      <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank">Firefox</a></li>
      <li><a href="https://help.opera.com/en/latest/web-preferences/#cookies" target="_blank">Opera</a></li>
      <li><a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank">Safari</a></li>
    </ul>
  `;
  
  termsContent = `
    <h3>Terms of Service</h3>
    <p>Last updated: ${new Date().toLocaleDateString()}</p>
    
    <h4>Legal Notice</h4>
    <p>On our website, we present general information about MZM Homes, its operations, and products. Please read this notice carefully, which applies to everyone who visits this website. By using the website, the user confirms that they have accepted and agree with the terms described here.</p>
    
    <h4>Legal Notice of the Website</h4>
    <p>All rights reserved. This notice applies to the homepage and all subpages of the website.</p>
    
    <p>All information and materials (written and visual) on this website are, within the legally permitted framework, subject to copyright protection or other forms of intellectual property protection of MZM Homes.</p>
    
    <p>The user may use the content on the website exclusively for their personal, non-commercial use. Any other form of use of the content of this website for commercial purposes is prohibited.</p>
    
    <h4>Limitation of Use of Information and Materials</h4>
    <p>All information and materials (written and visual) published on the website may be reproduced only for non-commercial purposes, while maintaining all stated warnings about copyright or other rights.</p>
    
    <p>Information and materials published on these web pages may not be transcribed, reproduced, or otherwise distributed for commercial purposes without the express written permission of MZM Homes. Each reproduction or copy of the content of these web pages must use the MZM Homes mark, in accordance with the rules of corporate visual identity - all rights reserved.</p>
    
    <p>MZM Homes may change this legal notice at any time and it is binding for users in its amended form, so we recommend that you monitor it on the website in case of use. By using these pages, you have demonstrated and confirmed that you are familiar with our terms and agree with them.</p>
    
    <p>Use of the website contrary to the stated rules is prohibited. A user who uses the website in a manner that is contrary to the stated rules is fully materially and compensatorily liable.</p>
    
    <h4>Limitation of Liability</h4>
    <p>All information and materials on the website are of an informative nature. MZM Homes strives for the correctness, timeliness, and completeness of data on the website, but cannot guarantee their accuracy and completeness and assumes no responsibility.</p>
    
    <p>Users use the published content at their own risk. Neither MZM Homes, nor any legal or natural person who participated in the creation and production of the website or is still involved in upgrades, is therefore not responsible for any damage that would result from access to, use of, or inability to use information on these web pages or for any errors or deficiencies in their content.</p>
    
    <p>MZM Homes also cannot be responsible for damage (including those caused by viruses) to computer equipment, mobile phones, or any other application with which it is possible to access the website due to the user's visit to the page or any of its use.</p>
    
    <p>MZM Homes is not responsible for any damage of any kind that might arise from the use or inability to use the website.</p>
    
    <p>MZM Homes reserves the right to change the content of the website at any time, in any way, and regardless of the reason without prior notice, and assumes no responsibility for any consequences of such changes.</p>
    
    <h4>Contact</h4>
    <p>If you have any questions about these terms of service, please contact us at:</p>
    <p>MZM Homes<br>
    Temes u. 18, 3528 Miskolc, HUNGARY<br>
    Email: info@mzmhomes.com</p>
  `;

  constructor(
    private translationService: TranslationService,
    private emailService: EmailService
  ) {
    // Initialize with current language values
    this.infoEmail = this.emailService.getInfoEmail();
    this.officeEmail = this.emailService.getOfficeEmail();
  }

  ngOnInit(): void {
    // Subscribe to language changes to update emails
    this.languageSubscription =
      this.translationService.currentLanguage$.subscribe(() => {
        this.infoEmail = this.emailService.getInfoEmail();
        this.officeEmail = this.emailService.getOfficeEmail();
      });
  }

  ngOnDestroy(): void {
    // Clean up subscription
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
      this.languageSubscription = null;
    }
  }

  // Method to translate text
  translate(key: string): string {
    return this.translationService.translate(key);
  }
  
  // Open privacy policy modal
  openPrivacyModal(): void {
    this.showPrivacyModal = true;
  }
  
  // Open terms of service modal
  openTermsModal(): void {
    this.showTermsModal = true;
  }
  
  // Open cookie policy modal
  openCookieModal(): void {
    this.showCookieModal = true;
  }
  
  // Close modals
  closeModals(): void {
    this.showPrivacyModal = false;
    this.showTermsModal = false;
    this.showCookieModal = false;
  }
}
